/**
 * O outro participante SAIU DE PROPÓSITO (clicou em "Sair") ou só caiu? Para quem fica na sala as duas coisas
 * somem do palco do mesmo jeito, mas não são a mesma coisa: quem caiu "está reconectando"; quem saiu não está, e
 * dizer "Reconectando…" faz parecer que o problema é da conexão.
 *
 * O LiveKit diz o motivo no evento `ParticipantDisconnected`: `CLIENT_INITIATED` é a pessoa saindo. Qualquer
 * outro motivo (queda, tempo esgotado) ou a ausência dele (o servidor não informou, ou a saída foi percebida numa
 * ressincronização) continua valendo como queda: o aviso antigo de "Reconectando…". Volta ao normal quando o
 * participante entra de novo.
 *
 * O transcritor da Jev (`tx:`) entra e sai da sala sem ser uma pessoa: a saída dele (kill switch, sessão que
 * reinicia) nunca vale como "o contato saiu".
 *
 * Vive fora do que remonta (ver `PeerLeftTracker`): mandar a sala para a janela flutuante recria a árvore e não
 * pode esquecer que o outro lado já saiu.
 */
import { useEffect, useState } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { DisconnectReason, RoomEvent } from 'livekit-client';
import { isTranscriberIdentity } from '../transcript/transcriptProtocol';

export function usePeerLeftOnPurpose() {
    const room = useRoomContext();
    const [left, setLeft] = useState(false);

    useEffect(() => {
        if (!room) return undefined;
        const onDisconnected = (participant, reason) => {
            if (isTranscriberIdentity(participant?.identity)) return;
            setLeft(reason === DisconnectReason.CLIENT_INITIATED);
        };
        const onConnected = (participant) => {
            if (isTranscriberIdentity(participant?.identity)) return;
            setLeft(false);
        };
        room.on(RoomEvent.ParticipantDisconnected, onDisconnected);
        room.on(RoomEvent.ParticipantConnected, onConnected);
        return () => {
            room.off(RoomEvent.ParticipantDisconnected, onDisconnected);
            room.off(RoomEvent.ParticipantConnected, onConnected);
        };
    }, [room]);

    return left;
}

/** Sem desenho: só avisa quem montou a sala se o outro lado saiu de propósito. */
export function PeerLeftTracker({ onChange }) {
    const left = usePeerLeftOnPurpose();
    useEffect(() => { onChange(left); }, [left, onChange]);
    return null;
}
